import {
  TrendingUp,
  CheckCircle2,
  Calculator,
  Users as UsersIcon,
  BarChart3,
  UserMinus,
  UserCheck,
  LogIn,
  Wrench,
  Database,
  UserPlus,
  Calendar,
} from "lucide-react";

const TIMELINE_BLOCKS = [
  { order: 0, name: "Tu punto de partida", weeks: 2 },
  { order: 1, name: "Polaris", weeks: 3 },
  { order: 2, name: "Núcleo", weeks: 3 },
  { order: 3, name: "Sistema de Fidelización PRO", weeks: 2 },
  { order: 4, name: "Libera tu tiempo", weeks: 2 },
];
const TOTAL_WEEKS = TIMELINE_BLOCKS.reduce((s, b) => s + b.weeks, 0);

function KpiCard({ icon: Icon, label, value, color, extra }) {
  return (
    <div className="p-3 bg-white rounded-xl border border-gray-100">
      <div className={`flex items-center gap-2 ${color} mb-1`}>
        <Icon size={16} />
        <span className="text-xs font-medium">{label}</span>
      </div>
      <p className="text-xl font-bold text-gray-900">
        {value}
        {extra && (
          <span className="text-xs font-normal text-gray-500 ml-1">
            {extra}
          </span>
        )}
      </p>
    </div>
  );
}

function formatDate(iso, includeTime = false) {
  if (!iso) return "—";
  const opts = { day: "numeric", month: "short", year: "numeric" };
  if (includeTime) {
    opts.hour = "2-digit";
    opts.minute = "2-digit";
  }
  return new Date(iso).toLocaleDateString("es-ES", opts);
}

function isBlockComplete(order, activities) {
  const items = activities.filter((a) => a.block_order === order);
  return items.length > 0 && items.every((a) => a.completed);
}

function ProgramStrip({ startDate, activities }) {
  if (!startDate) {
    return (
      <p className="text-sm text-gray-400">Sin fecha de inicio configurada</p>
    );
  }

  const diff = Date.now() - new Date(startDate).getTime();
  const weeksElapsed = Math.max(0, diff / (7 * 24 * 60 * 60 * 1000));
  const currentWeek = Math.min(TOTAL_WEEKS, Math.ceil(weeksElapsed));
  const weeksLeft = Math.max(0, Math.ceil(TOTAL_WEEKS - weeksElapsed));

  let accWeeks = 0;
  let expectedBlock = TIMELINE_BLOCKS[TIMELINE_BLOCKS.length - 1].order;
  for (const b of TIMELINE_BLOCKS) {
    accWeeks += b.weeks;
    if (weeksElapsed < accWeeks) {
      expectedBlock = b.order;
      break;
    }
  }

  const timePct = Math.min(100, Math.round((weeksElapsed / TOTAL_WEEKS) * 100));

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium text-gray-900">
          Semana {currentWeek} de {TOTAL_WEEKS}
        </span>
        <span className="text-gray-500">
          {weeksLeft > 0 ? `${weeksLeft} sem restantes` : "Programa completado"}
        </span>
      </div>

      {/* Time progress bar */}
      <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-indigo-500 rounded-full transition-all"
          style={{ width: `${timePct}%` }}
        />
      </div>

      {/* Blocks strip */}
      <div className="grid grid-cols-5 gap-1.5">
        {TIMELINE_BLOCKS.map((b) => {
          const done = isBlockComplete(b.order, activities);
          const isExpected = b.order === expectedBlock;
          return (
            <div
              key={b.order}
              className={`px-2 py-2 rounded-lg text-center text-xs border ${
                done
                  ? "bg-emerald-50 border-emerald-300 text-emerald-700"
                  : isExpected
                  ? "bg-indigo-50 border-indigo-300 text-indigo-700"
                  : "bg-gray-50 border-gray-200 text-gray-500"
              }`}
            >
              <span className="font-medium block truncate">{b.name}</span>
              <span className="text-[10px]">
                {done ? "Completado" : isExpected ? "Debería estar" : `${b.weeks} sem`}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function OverviewTab({ student, data }) {
  const { activities, logins, followups, simulations, leads, bajas, monthlyData } = data;

  const isAirtable = student.airtable_id && !student.airtable_id.startsWith("admin_");

  const completedCount = activities.filter((a) => a.completed).length;
  const progressPct =
    activities.length > 0
      ? Math.round((completedCount / activities.length) * 100)
      : 0;

  const followupsCompleted = followups.filter((f) => f.completed).length;
  const bajasRecuperadas = bajas.filter((b) => b.resultado === "Recuperado").length;
  const toolsActive =
    simulations.length + leads.length + bajas.length + monthlyData.length > 0;

  const timeline = [];
  activities
    .filter((a) => a.completed && a.completed_at)
    .forEach((a) => {
      timeline.push({
        date: a.completed_at,
        type: "activity",
        label: a.name,
        block: a.block_name,
      });
    });
  logins.forEach((l) => {
    timeline.push({ date: l.logged_in_at, type: "login", label: "Inicio de sesión" });
  });
  timeline.sort((a, b) => new Date(b.date) - new Date(a.date));
  const recentTimeline = timeline.slice(0, 30);

  return (
    <div className="space-y-6">
      {/* Origin + Program */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Calendar size={16} className="text-indigo-600" />
            <span className="text-sm font-semibold text-gray-900">
              Programa del alumno
            </span>
          </div>
          <div className="flex items-center gap-2">
            {isAirtable ? (
              <span className="flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-full bg-orange-100 text-orange-700">
                <Database size={11} /> Airtable
              </span>
            ) : (
              <span className="flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-full bg-indigo-100 text-indigo-700">
                <UserPlus size={11} /> Invitación manual
              </span>
            )}
            {student.start_date && (
              <span className="text-xs text-gray-400">
                Inicio: {formatDate(student.start_date)}
              </span>
            )}
          </div>
        </div>
        <ProgramStrip startDate={student.start_date} activities={activities} />
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard icon={TrendingUp} label="Progreso" value={`${progressPct}%`} color="text-indigo-600" />
        <KpiCard icon={CheckCircle2} label="Actividades" value={`${completedCount}/${activities.length}`} color="text-emerald-600" />
        <KpiCard icon={Calculator} label="Simulaciones" value={simulations.length} color="text-blue-600" />
        <KpiCard icon={UsersIcon} label="Leads" value={leads.length} color="text-purple-600" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard icon={BarChart3} label="Métricas" value={monthlyData.length} extra="meses" color="text-amber-600" />
        <KpiCard icon={UserMinus} label="Bajas" value={bajas.length} extra={bajasRecuperadas > 0 ? `(${bajasRecuperadas} recup.)` : undefined} color="text-red-600" />
        <KpiCard icon={UserCheck} label="Seguimientos" value={`${followupsCompleted}/${followups.length}`} color="text-teal-600" />
        <KpiCard icon={Wrench} label="Herramientas" value={toolsActive ? "Activo" : "Sin uso"} color="text-slate-600" />
      </div>

      {/* Login stats */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center gap-2 text-cyan-600 mb-3">
          <LogIn size={16} />
          <span className="text-sm font-semibold">Historial de Accesos</span>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <p className="text-xs text-gray-500 mb-0.5">Total logins</p>
            <p className="text-lg font-bold text-gray-900">{logins.length}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-0.5">Primer acceso</p>
            <p className="text-sm font-medium text-gray-900">
              {logins.length > 0 ? formatDate(logins[0].logged_in_at) : "—"}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-0.5">Último acceso</p>
            <p className="text-sm font-medium text-gray-900">
              {logins.length > 0
                ? formatDate(logins[logins.length - 1].logged_in_at, true)
                : "—"}
            </p>
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-4">
          Actividad reciente
        </h3>
        {recentTimeline.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-6">
            Sin actividad registrada
          </p>
        ) : (
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {recentTimeline.map((item, i) => (
              <div key={i} className="flex items-start gap-3">
                <div
                  className={`mt-1 w-2 h-2 rounded-full shrink-0 ${
                    item.type === "activity" ? "bg-emerald-500" : "bg-cyan-400"
                  }`}
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-700 truncate">{item.label}</p>
                  {item.block && (
                    <span className="text-xs text-gray-400">{item.block}</span>
                  )}
                </div>
                <span className="text-xs text-gray-400 shrink-0">
                  {formatDate(item.date, true)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
